import { Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AppBootstrapService, OnlineQuoteToolModeService } from './services';
import { LoadingService, LoadingSpinnerComponent } from '@lx/shared/ui/loading-spinner';
import { FocusTrapModule } from 'primeng/focustrap';
import { ScrollTopOnNavigateDirective } from '@lx/shared/ui/directives';
import { GoogleAnalyticsClickEventDirective } from '@lx/shared/google-analytics';
import { GoogleAnalyticsEventEnum } from './google-analytics';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { toSignal } from '@angular/core/rxjs-interop';
import { AcquisitionDisclaimerWidgetService } from '@lx/quote-tool/shared/services';
import { ReturnUrlService } from '@lx/quote-tool/lite/shared/services';

/**
 * Root component for Online Quote Tool
 *
 * Responsibilities:
 * - Initialize UI behaviors (after APP_INITIALIZER completes)
 * - Navigate to appropriate step based on mode (default or deep link)
 *
 * Architecture:
 * - APP_INITIALIZER handles data/state initialization
 * - ngOnInit handles UI behaviors and navigation
 *
 * @see AppInitializerService for initialization logic
 * @see OnlineQuoteToolModeService for navigation logic
 */
@Component({
  standalone: true,
  selector: 'lx-online-quote-tool-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, LoadingSpinnerComponent, FocusTrapModule, ScrollTopOnNavigateDirective, GoogleAnalyticsClickEventDirective],
})
export class AppComponent implements OnInit {
  @ViewChild('acquisitionDisclaimerContainer', { static: true }) acquisitionDisclaimerContainer: ElementRef<HTMLElement> | undefined;

  private readonly modeService = inject(OnlineQuoteToolModeService);
  private readonly appBootstrapService = inject(AppBootstrapService);
  private readonly loadingService = inject(LoadingService);
  private readonly breakpointObserverService = inject(BreakpointObserverService);
  private readonly acquisitionDisclaimerWidgetService = inject(AcquisitionDisclaimerWidgetService);
  private readonly returnUrlService = inject(ReturnUrlService);

  protected loadingInfo = this.loadingService.loading;

  protected desktop = toSignal(this.breakpointObserverService.desktop$);
  protected withBackButton = signal(this.returnUrlService.hasReturnUrl());
  protected backButtonLabel = signal(this.returnUrlService.getBackButtonLabel());
  protected returnUrl = signal(this.returnUrlService.getReturnUrl());

  protected readonly closeDataLayerEventName = GoogleAnalyticsEventEnum.QuoteSaveAndClose;

  /**
   * Initialize UI behaviors and perform navigation
   *
   * This runs AFTER APP_INITIALIZER has completed:
   * - Store is already initialized with data
   * - Country/currency are already loaded
   * - Router is ready for navigation
   *
   * Responsibilities:
   * 1. Navigate based on mode strategy (default or deep link)
   * 2. Bootstrap UI behaviors (breakpoint observer, etc.)
   */
  ngOnInit(): void {
    // Navigate using the mode service strategy
    // This will navigate to:
    // - currentStepId (if exists in session storage) OR first step (default mode)
    // - Associated step to deep links (if accessing via deep link)
    this.modeService.navigate().subscribe();

    // Initialize UI behaviors (breakpoint observer, etc.)
    this.appBootstrapService.bootstrapUiBehaviors();
    this.acquisitionDisclaimerWidgetService.loadWidget(this.acquisitionDisclaimerContainer);
  }
}
